import { FC, useEffect } from 'react'
import { SelectionField } from '../../../../generated'
import dayjs from 'dayjs'
import { FormControl, TextField } from '@mui/material'
import { DatePicker as MuiDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

interface DateRangePickerProps {
  field: SelectionField
  reportFilters: Record<string, any>
  setReportFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>
}

const DatePicker: FC<DateRangePickerProps> = ({ field, reportFilters, setReportFilters }) => {
  const fieldName = field.field
  const fieldOperator = field.operator ?? 'lte'
  const minDate = field.minDate ? dayjs(field.minDate) : undefined
  const handleDateConfirm = (statementMonth: dayjs.Dayjs | null) => {
    setReportFilters(prev => ({
      ...prev,
      [fieldName]: {
        [fieldOperator]: statementMonth?.hour(23).format()
      }
    }))
  }

  // Re-seed today whenever the value goes missing, not only on mount: switching between two reports
  // that share this field (e.g. the matter lists' To Date) keeps this instance mounted while
  // ReportWidget clears reportFilters, which would otherwise leave the date empty. Runs on every
  // filters change, since that clear can land after this effect has already seeded.
  const hasDate = Boolean(reportFilters?.[fieldName]?.[fieldOperator])

  useEffect(() => {
    if (hasDate) return
    setReportFilters(prev => ({
      ...prev,
      [fieldName]: {
        [fieldOperator]: dayjs().hour(23).format()
      }
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, hasDate, reportFilters])

  if (field?.type !== 'DATE') return null

  return (
    <FormControl variant="outlined">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDatePicker
          label={field?.label || ''}
          value={reportFilters?.[fieldName]?.[fieldOperator] ?? null}
          minDate={minDate}
          onChange={value => {
            handleDateConfirm(value)
          }}
          renderInput={params => <TextField {...params} inputProps={{ ...params.inputProps, readOnly: true }} />}
        />
      </LocalizationProvider>
    </FormControl>
  )
}

export default DatePicker
